/*
 * =============================================================================
 * REMASS COLOUR GALLERY FILTER - PILOT THEME MODIFICATION
 * Added: 28 July 2026
 * Theme: REMASS TEST | Colour gallery pilot 2026-07-28
 *
 * Purpose:
 * - Filter the large Flickity gallery, thumbnail carousel and Magnific Popup
 *   to the images mapped to the currently selected "colour" option.
 *
 * Required product data:
 * - Product metafield: custom.colour_media_map
 * - Metafield type: JSON
 * - Option name: colour (case-insensitive)
 *
 * Safety / fallback:
 * - Products without the metafield use the original full gallery.
 * - Products without a colour option, including products without selectable
 *   variants, use the original full gallery.
 * - Missing, malformed or unmatched media IDs fall back to all product media.
 *
 * Scope:
 * - Added to the unpublished pilot theme only. The live theme was not changed.
 * - Added code is marked with REMASS COLOUR GALLERY FILTER START/END comments.
 * =============================================================================
 */

class FlickityLarge extends HTMLElement {
  constructor() {
    super();

    this.flickityTop = this.querySelector("[id*='flickity_carousel_product_']");
    this.noButtons = this.hasAttribute("no-buttons");
    this.flickityMagnifier = this.hasAttribute("flickity-magnifier");
    // ===== REMASS COLOUR GALLERY FILTER: START =====
    // Capture the product section and original media cells before Flickity
    // moves them into its generated viewport/slider structure.
    this.productRoot =
      this.closest("[id^='shopify-section-']") ||
      this.closest("section") ||
      document;

    this.allCells = this.flickityTop
      ? Array.from(this.flickityTop.children)
      : [];
    this.allMediaIds = this.allCells
      .map((cell) => String(cell.dataset.mediaId || ""))
      .filter(Boolean);
    this.allImageUrls = this.allCells
      .map((cell) => cell.dataset.imageUrl || "")
      .filter(Boolean);
    this.imageUrls = [...this.allImageUrls];
    this.colourMediaMap = this.parseColourMediaMap();
    this.activeMediaIds = [...this.allMediaIds];
    this.activeMediaSignature = null;
    this.mfpObj = null;
    this.isRebuilding = false;
    // ===== REMASS COLOUR GALLERY FILTER: END =====

  }

  connectedCallback() {
    if (!this.flickityTop) return;
    if (this.noButtons) this.initialise();
    else this.deferInitialisation();
  }

  // ===== REMASS COLOUR GALLERY FILTER: START =====
  // Listener cleanup, option normalisation and JSON map parsing.
  disconnectedCallback() {
    if (this.initialisationObserver) this.initialisationObserver.disconnect();
    if (this.productRoot && this.boundProductChange) {
      this.productRoot.removeEventListener("change", this.boundProductChange);
    }
  }

  deferInitialisation() {
    if (this.flickity) return;
    if (!('IntersectionObserver' in window)) {
      this.initialise();
      return;
    }

    this.initialisationObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.initialisationObserver.disconnect();
        this.initialise();
      },
      { rootMargin: '600px 0px' }
    );
    this.initialisationObserver.observe(this);
  }

  initialise() {
    if (!this.flickityTop || this.flickity) return;
    if (this.initialisationObserver) this.initialisationObserver.disconnect();
    this.removeAttribute('data-deferred-carousel');
    this.createFlickity();

    if (this.noButtons) {
      this.handleNoButtons();
      this.bindColourFiltering();
      window.requestAnimationFrame(() => this.applySelectedColour(true));
    } else {
      this.buttonsClickListener();
    }
  }

  normaliseOptionValue(value) {
    return String(value || "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
  }

  parseColourMediaMap() {
    const mapElement = this.querySelector("[data-colour-media-map]");
    if (!mapElement) return null;

    try {
      let parsed = JSON.parse(mapElement.textContent);
      if (typeof parsed === "string") parsed = JSON.parse(parsed);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        return null;
      }

      return Object.fromEntries(
        Object.entries(parsed).map(([colour, mediaIds]) => [
          this.normaliseOptionValue(colour),
          Array.isArray(mediaIds)
            ? mediaIds.map((mediaId) => String(mediaId))
            : [],
        ])
      );
    } catch (error) {
      console.error(
        "[MassTech / Colour Gallery] Could not parse colour media map.",
        error
      );
      return null;
    }
  }
  // ===== REMASS COLOUR GALLERY FILTER: END =====

  createFlickity() {
    this.flickity = new Flickity(this.flickityTop, {
      contain: true,
      imagesLoaded: true,
      lazyLoad: 1,
      wrapAround: true,
      pageDots: false,
      adaptiveHeight: true,
      on: {
        ready: () => {
          this.removeAttribute("hidden");
        },
      },
    });

    this.flickity.resize();
    console.log(
      `%c[MassTech / Flickity Custom] flickity large object initialised ${
        this.noButtons ? "no buttons" : "buttons"
      }`,
      "color: green"
    );
  }

  buttonsClickListener() {
    const slider = this.querySelector(".flickity-slider");
    if (!slider || !this.dataset.productUrl) return;

    slider.addEventListener("click", () => {
      window.location.href = this.dataset.productUrl;
    });
  }

  handleNoButtons() {
    const carouselCell = this.querySelector(
      "[class*='flickity-carousel-cell-class']"
    );
    if (!carouselCell || typeof MFP === "undefined") return;

    // ===== REMASS COLOUR GALLERY FILTER: START =====
    // imageUrls is rebuilt whenever colour changes, keeping the fullscreen
    // Magnific Popup gallery in sync with the visible Flickity cells.
    // ===== REMASS COLOUR GALLERY FILTER: END =====
    this.mfpObj = new MFP(
      "carousel-cell",
      "[class*='" +
        carouselCell.classList[0] +
        " carousel-cell is-selected']",
      this.flickity,
      this.mfpMetroMag,
      this.imageUrls
    );
    this.mfpObj.create();

    this.flickity.on("change", (index) => {
      if (this.mfpObj && !this.isRebuilding) this.mfpObj.setIndex(index);
    });

    this.flickity.on("settle", function () {
      // Reserved for the optional magnifier.
    });
  }

  // ===== REMASS COLOUR GALLERY FILTER: START =====
  // Detect the selected colour, resolve its media IDs, rebuild the carousel,
  // and notify the thumbnail carousel. No map means no behaviour change.
  bindColourFiltering() {
    if (!this.colourMediaMap || !this.productRoot) return;

    this.boundProductChange = () => {
      window.requestAnimationFrame(() => this.applySelectedColour());
    };
    this.productRoot.addEventListener("change", this.boundProductChange);
  }

  getSelectedColour() {
    const optionIndex = Number.parseInt(
      this.dataset.colourOptionIndex || "-1",
      10
    );
    const picker = this.productRoot.querySelector(
      "variant-radios, variant-selects"
    );

    if (picker && optionIndex >= 0) {
      if (picker.matches("variant-radios")) {
        const fieldsets = Array.from(picker.querySelectorAll("fieldset"));
        const checked = fieldsets[optionIndex]?.querySelector("input:checked");
        if (checked) return this.normaliseOptionValue(checked.value);
      } else {
        const selects = Array.from(picker.querySelectorAll("select"));
        if (selects[optionIndex]) {
          return this.normaliseOptionValue(selects[optionIndex].value);
        }
      }
    }

    const namedRadio = Array.from(
      this.productRoot.querySelectorAll("input[type='radio']:checked")
    ).find((input) => this.normaliseOptionValue(input.name) === "colour");
    if (namedRadio) return this.normaliseOptionValue(namedRadio.value);

    const namedSelect = Array.from(
      this.productRoot.querySelectorAll("select")
    ).find(
      (select) =>
        this.normaliseOptionValue(select.name) === "options[colour]" ||
        this.normaliseOptionValue(select.name) === "colour"
    );

    return namedSelect
      ? this.normaliseOptionValue(namedSelect.value)
      : null;
  }

  applySelectedColour(force = false) {
    if (!this.colourMediaMap || !this.flickity) return;

    const colour = this.getSelectedColour();
    const mappedMediaIds =
      colour && Array.isArray(this.colourMediaMap[colour])
        ? this.colourMediaMap[colour]
        : [];
    const mediaIds = mappedMediaIds.length
      ? mappedMediaIds
      : this.allMediaIds;
    const signature = mediaIds.join(",");

    if (!force && signature === this.activeMediaSignature) return;

    this.activeMediaSignature = signature;
    this.activeMediaIds = [...mediaIds];
    this.rebuildForMediaIds(mediaIds);

    this.productRoot.dispatchEvent(
      new CustomEvent("remass:colour-media-filter", {
        detail: {
          colour,
          mediaIds: [...this.activeMediaIds],
        },
      })
    );
  }

  rebuildForMediaIds(mediaIds) {
    const allowed = new Set(mediaIds.map((mediaId) => String(mediaId)));
    let nextCells = this.allCells.filter((cell) =>
      allowed.has(String(cell.dataset.mediaId || ""))
    );

    if (!nextCells.length) nextCells = [...this.allCells];

    const currentCells = this.flickity.getCellElements();
    const sameCells =
      currentCells.length === nextCells.length &&
      currentCells.every((cell, index) => cell === nextCells[index]);

    this.isRebuilding = true;

    if (!sameCells) {
      if (currentCells.length) this.flickity.remove(currentCells);
      if (nextCells.length) this.flickity.append(nextCells);
    }

    this.flickity.select(0, false, true);
    this.flickity.resize();
    this.isRebuilding = false;

    this.imageUrls = nextCells
      .map((cell) => cell.dataset.imageUrl || "")
      .filter(Boolean);

    if (this.mfpObj) {
      this.mfpObj.img_urls = [...this.imageUrls];
      this.mfpObj.create();
    }
  }
  // ===== REMASS COLOUR GALLERY FILTER: END =====
}

customElements.define("flickity-large", FlickityLarge);

class FlickitySmall extends HTMLElement {
  constructor() {
    super();

    this.flickity = null;
    this.flickityTop = this.querySelector(
      ".carousel.product__image-thumbnail"
    );
    // ===== REMASS COLOUR GALLERY FILTER: START =====
    // Keep an immutable reference to every thumbnail so filtered cells can be
    // removed and restored without recreating their markup.
    this.productRoot =
      this.closest("[id^='shopify-section-']") ||
      this.closest("section") ||
      document;
    this.allCells = this.flickityTop
      ? Array.from(this.flickityTop.children)
      : [];

    this.boundColourFilter = (event) => {
      this.rebuildForMediaIds(event.detail?.mediaIds || []);
    };
    // ===== REMASS COLOUR GALLERY FILTER: END =====
  }

  connectedCallback() {
    if (!this.flickityTop) return;

    this.createFlickity();

    // ===== REMASS COLOUR GALLERY FILTER: START =====
    // Synchronise thumbnails with the large gallery on initial load and on
    // every subsequent colour change.
    this.productRoot.addEventListener(
      "remass:colour-media-filter",
      this.boundColourFilter
    );

    window.requestAnimationFrame(() => {
      const largeGallery = this.productRoot.querySelector("flickity-large");
      if (largeGallery?.activeMediaIds?.length) {
        this.rebuildForMediaIds(largeGallery.activeMediaIds);
      }
    });
    // ===== REMASS COLOUR GALLERY FILTER: END =====
  }

  // ===== REMASS COLOUR GALLERY FILTER: START =====
  disconnectedCallback() {
    if (this.productRoot) {
      this.productRoot.removeEventListener(
        "remass:colour-media-filter",
        this.boundColourFilter
      );
    }
  }

  createFlickity() {
    this.flickity = new Flickity(this.flickityTop, {
      asNavFor: ".carousel.product__image-slider",
      contain: true,
      imagesLoaded: true,
      lazyLoad: 4,
      pageDots: false,
      prevNextButtons: false,
    });
  }

  rebuildForMediaIds(mediaIds) {
    if (!this.flickity || !mediaIds.length) return;

    const allowed = new Set(mediaIds.map((mediaId) => String(mediaId)));
    let nextCells = this.allCells.filter((cell) =>
      allowed.has(String(cell.dataset.mediaId || ""))
    );

    if (!nextCells.length) nextCells = [...this.allCells];

    const currentCells = this.flickity.getCellElements();
    const sameCells =
      currentCells.length === nextCells.length &&
      currentCells.every((cell, index) => cell === nextCells[index]);

    this.isRebuilding = true;

    if (!sameCells) {
      if (currentCells.length) this.flickity.remove(currentCells);
      if (nextCells.length) this.flickity.append(nextCells);
    }

    this.flickity.select(0, false, true);
    this.flickity.resize();

    if (typeof this.flickity.navCompanionSelect === "function") {
      this.flickity.navCompanionSelect(true);
    }
  }
  // ===== REMASS COLOUR GALLERY FILTER: END =====
}

customElements.define("flickity-small", FlickitySmall);
